import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Flame, X, Plus } from "lucide-react";
import MealPlanForm from "./MealPlanForm";
import MealPlanDetailsDialog from "./MealPlanDetailsDialog";

const goalLabels = {
  bulking: "Bulking",
  cutting: "Cutting",
  maintenance: "Maintenance",
  clean_eating: "Clean Eating",
};

const goalColors = {
  bulking: "bg-primary/10 text-primary",
  cutting: "bg-destructive/10 text-destructive",
  maintenance: "bg-emerald-500/10 text-emerald-500",
  clean_eating: "bg-amber-500/10 text-amber-500",
};

export default function DayMealPlanDialog({ open, onOpenChange, day, dayName, userId }) {
  const [search, setSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: weeklyMealPlan = null } = useQuery({
    queryKey: ["userWeeklyMealPlan", userId],
    queryFn: () =>
      base44.entities.UserWeeklyMealPlan.filter({ user_id: userId }).then(
        (results) => results[0] || null
      ),
    enabled: !!userId,
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["mealPlanTemplates"],
    queryFn: () => base44.entities.MealPlanTemplate.list(),
  });

  const currentTemplateId = weeklyMealPlan?.[day];
  const currentTemplate = currentTemplateId ? templates.find((t) => t.id === currentTemplateId) : null;

  const updateWeeklyMealPlan = async (templateId) => {
    if (!weeklyMealPlan) {
      await base44.entities.UserWeeklyMealPlan.create({
        user_id: userId,
        [day]: templateId,
      });
    } else {
      await base44.entities.UserWeeklyMealPlan.update(weeklyMealPlan.id, {
        [day]: templateId,
      });
    }
  };

  const assignMutation = useMutation({
    mutationFn: (templateId) => updateWeeklyMealPlan(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userWeeklyMealPlan"] });
      onOpenChange(false);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => updateWeeklyMealPlan(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userWeeklyMealPlan"] });
    },
  });

  const filteredTemplates = templates.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[calc(100%-2rem)] md:w-full rounded-xl md:rounded-lg">
        <DialogHeader>
          <DialogTitle>Assign Meal Plan for {dayName}</DialogTitle>
        </DialogHeader>
        <HealthSourceNote topic="planning" />

        {currentTemplate && (
          <div className="mb-4 p-3 rounded-lg bg-muted/50 border">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="font-semibold text-sm">{currentTemplate.name}</p>
                {currentTemplate.goal && (
                  <Badge
                    variant="secondary"
                    className={`text-[10px] mt-1 ${goalColors[currentTemplate.goal]}`}
                  >
                    {goalLabels[currentTemplate.goal]}
                  </Badge>
                )}
                {currentTemplate.daily_calories && (
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Flame className="w-3 h-3" />
                    {currentTemplate.daily_calories} kcal
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => removeMutation.mutate()}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <div>
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-3"
          />
          <ScrollArea className="h-64">
            <div className="space-y-2 pr-4">
              {filteredTemplates.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8">
                  No templates available
                </p>
              ) : (
                filteredTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => {
                      setSelectedPlan(template);
                      setDetailsOpen(true);
                    }}
                    disabled={assignMutation.isPending}
                    className="w-full text-left p-3 rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-transparent hover:border-primary/50"
                  >
                    <p className="font-medium text-sm">{template.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {template.goal && (
                        <Badge
                          variant="secondary"
                          className={`text-[10px] ${goalColors[template.goal]}`}
                        >
                          {goalLabels[template.goal]}
                        </Badge>
                      )}
                      {template.daily_calories && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Flame className="w-3 h-3" />
                          {template.daily_calories} kcal
                        </div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>

      {selectedPlan && (
        <MealPlanDetailsDialog
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          plan={selectedPlan}
          onAssign={async () => {
            await assignMutation.mutateAsync(selectedPlan.id);
            setDetailsOpen(false);
            setSelectedPlan(null);
          }}
          onCancel={() => {
            setDetailsOpen(false);
            setSelectedPlan(null);
          }}
        />
      )}
    </Dialog>
  );
}